import React from 'react'
import { observer } from 'mobx-react'
import styles from './JobProgressionExtensionDocumentCreationModal.sass'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { useDropzone } from 'hooks/use-dropzone'
import FileButton from 'components/FileButton'
import { DocumentCreationItem } from 'documents/views/DocumentCreationModal/DocumentCreationItem'
import JobProgressionViewModel from '../JobProgressionViewModel'

interface Props {
  vm: JobProgressionViewModel
}

export default observer(function JobProgressionExtensionDocumentCreationModal({
  vm,
}: Props) {
  const documentCreationVm = vm.jobProgressionDocumentCreationVM!

  function onDrop(files: FileList) {
    documentCreationVm.addFiles(files)
  }

  function onEnter() {
    documentCreationVm.setDragging()
  }

  function onLeave() {
    documentCreationVm.stopDragging()
  }

  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  return (
    <ModalSheet
      ref={dropZoneRef}
      showing={documentCreationVm.showing}
      disableCloseOnOutsideClick={true}
      onRequestClose={documentCreationVm.close}
    >
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>
                  Upload extension
                  {vm.isExtensionRejected ? ' rejection ' : ' '}
                  confirmation
                </span>
              </div>
              <div className={styles.headerDescription}>
                <span>
                  Please mark the document as type 'Extension
                  {vm.isExtensionRejected ? ' Rejection ' : ' '}
                  Confirmation' to make it easier for the client to find.
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton onClick={documentCreationVm.close} />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        <div className={styles.creationItemList}>
          {documentCreationVm.documentCreationItemVMs.map(
            (docCreationItemVM, index) => (
              <div key={index}>
                <DocumentCreationItem
                  index={index}
                  vm={docCreationItemVM}
                  removeFile={documentCreationVm.removeFile}
                />
              </div>
            ),
          )}
          <div
            className={
              documentCreationVm.dragging
                ? styles.dropZoneItemDragging
                : styles.dropZoneItem
            }
          >
            <span className={styles.dropZoneTitle}>
              Drag and drop files here or&nbsp;
              <FileButton
                multiple
                onChange={documentCreationVm.addFiles}
                data-test="upload-extension-confirmation-documents-file-input"
              >
                <Button
                  className={styles.dropZoneButton}
                  borderless
                  type="text"
                >
                  Upload another file
                </Button>
              </FileButton>
            </span>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <div className={styles.footer}>
          <div className={styles.uploadButton}>
            <ModalSheetFooterButtonGroup>
              {!vm.isSkipUploadDisabled && (
                <Button
                  type="secondary"
                  disabled={vm.isSkipUploadDisabled}
                  onClick={() =>
                    vm.isExtensionRejectedOrWaitingOnClient
                      ? vm.uploadExtensionRejectedDocument(true)
                      : vm.answerExtensionConfirmed(true)
                  }
                >
                  Skip
                </Button>
              )}
              <Button
                type="primary"
                dataTest="upload-extension-confirmation-confirm-button"
                disabled={vm.isUploadDisabled}
                onClick={() =>
                  vm.isExtensionRejectedOrWaitingOnClient
                    ? vm.uploadExtensionRejectedDocument(false)
                    : vm.answerExtensionConfirmed(false)
                }
              >
                Upload Documents
              </Button>
            </ModalSheetFooterButtonGroup>
          </div>
        </div>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
